import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  //   Box,
  IconButton,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { generatePDF } from '../../../../../utils/reportUtils';
import { useCycle } from '../../../../../components/cycleContext/CycleContext';
import { usePatientContext } from '../../../../../components/patinetProvider';
import { EmbryoTransferType, getEmbryoTransfer } from '../../../../../components/Surgery/embryoTransfer/embryoTransfer.helpers';
import EmbryoTransferReport from './EmbryoTransferReport';

type Props = {
  open: boolean;
  onClose: () => void;
};

const EmbryoTransferPDFDialog: React.FC<Props> = ({ open, onClose }) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { id } = useParams();
  const [reportData, setReportData] = useState<EmbryoTransferType | null>(null);
  const { surgeryCycleId } = useCycle();
  const { MainpatientDetails } = usePatientContext();

  useEffect(() => {
    if (open && id && surgeryCycleId !== null) {
      getEmbryoTransfer(surgeryCycleId).then((data) => {
        setReportData(data);
      });
    }
  }, [open, id, surgeryCycleId]);

  const handleDownloadPDF = () => {
    if (reportRef.current && reportData) {
      const patientName = MainpatientDetails
        ? `${MainpatientDetails.first_name} ${MainpatientDetails.middle_name ?? ''} ${MainpatientDetails.last_name}`
          .replace(/\s+/g, '_')
          .trim()
        : 'Patient';

      const now = new Date();
      const formattedDate = now.toISOString().replace(/[:-]/g, '').slice(0, 15);

      const fileName = `${patientName}_Embryo_Transfer_Report_${formattedDate}`;

      generatePDF(reportRef.current, fileName);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <style>
        {`@media print {
          @page { size: landscape; }
          body * { visibility: hidden; }
          #printable-report, #printable-report * { visibility: visible; }
          #printable-report { position: absolute; left: 0; top: 0; width: 100%; }
          button, .MuiIconButton-root { display: none !important; }
        }`}
      </style>

      <DialogTitle sx={{ p: 3, pb: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Typography variant="h5" fontWeight={500}>Embryo Transfer Report</Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              onClick={handleDownloadPDF}
              sx={{
                backgroundColor: '#404040',
                color: 'white',
                '&:hover': { backgroundColor: '#505050' },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <DownloadIcon />
              Download Report
            </Button>
            <Button variant="outlined" onClick={handlePrint} sx={{ ml: 2 }}>
              Print Report
            </Button>
            <IconButton onClick={onClose} color="error" aria-label="Close Dialog">
              <HighlightOffIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Divider sx={{ mt: 3 }} />
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1, bgcolor: '#f9f9f9' }}>
        <div id="printable-report" ref={reportRef}>
          {reportData && <EmbryoTransferReport data={reportData} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmbryoTransferPDFDialog;
